import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import type { TemplateDef } from '../../promotions/types';
import type { FormValues } from '../../promotions/types';
import FormSection from '../../promotions/components/FormSection';
import { buildRenderedLayout } from '../../promotions/components/PromotionForm';

interface AgentFormPreviewProps {
  template: TemplateDef | null;
  values: FormValues;
  fieldErrors?: Record<string, string>;
  onFieldChange: (key: string, value: string | string[]) => void;
}

export default function AgentFormPreview({ template, values, fieldErrors, onFieldChange }: AgentFormPreviewProps) {
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});

  const handleToggleSection = (idx: number) => {
    setExpandedSections((prev) => ({ ...prev, [idx]: !(prev[idx] ?? true) }));
  };
  if (!template) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AssignmentOutlinedIcon sx={{ fontSize: 28, color: 'grey.400' }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
            Formulário de Briefing
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            Selecione um template no chat para ver o formulário aqui
          </Typography>
        </Box>
      </Box>
    );
  }

  const layout = template.id === 't3' ? buildRenderedLayout(template.id, template.sections, values) : null;
  const sections = layout
    ? [...layout.baseSections, ...layout.childGroups.flatMap((group) => group.sections.map((section) => ({
      ...section, title: `Card filho ${group.childNumber} — ${section.title}`,
    })))]
    : template.sections;

  const filledCount = Object.keys(values).filter((k) => {
    const v = values[k];
    return Array.isArray(v) ? v.length > 0 : v && v !== '';
  }).length;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              bgcolor: template.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.8rem' }}>
              {template.number}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {template.name}
            </Typography>
            <Chip
              label={template.mechanicsBadge}
              size="small"
              sx={{
                mt: 0.25,
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 600,
                bgcolor: `${template.color}15`,
                color: template.color,
              }}
            />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {filledCount} campo{filledCount !== 1 ? 's' : ''} preenchido{filledCount !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 2 },
        }}
      >
        <Paper
          elevation={0}
          sx={{ p: 0, bgcolor: 'transparent' }}
        >
          {sections.map((section, idx) => (
            <FormSection
              key={`${section.title}-${idx}`}
              section={section}
              index={idx}
              values={values}
              fieldErrors={fieldErrors}
              onChange={onFieldChange}
              accentColor={template.color}
              expanded={expandedSections[idx] ?? true}
              onToggle={() => handleToggleSection(idx)}
            />
          ))}
        </Paper>
      </Box>
    </Box>
  );
}
